/**
 * Comprobación de salud del libro (docs/PLAN-V4 §4). Editar la hoja a mano está permitido, así que la app
 * comprueba la integridad tras cada sincronización:
 * - bloqueantes → la app pasa a solo lectura y no calcula totales;
 * - avisos → calcula, pero señala las filas afectadas.
 * Cada problema puede llevar una reparación segura que la app ofrece (nunca se aplica sin preguntar).
 */
import { DIVIDED, SAVINGS_GOAL } from '@pf/domain';
import { normalizeFormula } from './cells.js';
import type { Workbook } from './parse.js';
import { FORMULAS, HEADERS, WORKBOOK_VERSION } from './schema.js';
import { USE_COL, USE_HEADER } from './write.js';

export type Repair =
  | { kind: 'rewrite-formulas'; cells: string[] }
  | { kind: 'assign-ids'; rows: number[] }
  | { kind: 're-id-duplicate'; rows: number[] }
  | { kind: 'clear-orphan-ids'; rows: number[] }
  | { kind: 'resume-archive' }
  | { kind: 'migrate-schema'; from: number; to: number }
  | { kind: 'add-notes-tab' }
  | { kind: 'compact-movements' };

export interface HealthIssue { level: 'blocking' | 'warning'; code: string; message: string; tab?: string; rows?: number[]; repair?: Repair }

export interface HealthReport { ok: boolean; blocking: HealthIssue[]; warnings: HealthIssue[] }

/** @param formulas fórmulas leídas en modo FORMULA, por celda ('Pestaña!A1'); se comparan las conocidas. */
export function checkWorkbook(wb: Workbook, formulas: Record<string, string> = {}): HealthReport {
  const issues: HealthIssue[] = [];
  const block = (i: Omit<HealthIssue, 'level'>) => issues.push({ level: 'blocking', ...i });
  const warn = (i: Omit<HealthIssue, 'level'>) => issues.push({ level: 'warning', ...i });

  // Versión
  const v = wb.control.version;
  if (v === null) block({ code: 'schema-version-missing', message: 'Falta la versión del libro en _Control' });
  else if (v > WORKBOOK_VERSION) block({ code: 'schema-version-newer', message: `El libro es de una versión más nueva (${v}) que la app (${WORKBOOK_VERSION}): actualiza la app` });
  else if (v < WORKBOOK_VERSION) block({ code: 'schema-version-older', message: `El libro está en la versión ${v}`, repair: { kind: 'migrate-schema', from: v, to: WORKBOOK_VERSION } });

  // Cabeceras
  for (const [tab, expected] of Object.entries(HEADERS)) {
    const have = wb.headers[tab];
    if (!have) continue;   // pestañas que no se leen en cada sincronización (Archivo, Histórico…)
    // Cuentas M («Uso» de la cuenta) no tenía título en libros anteriores: vale vacía o con «Uso»
    const bad = expected.findIndex((h, i) => (have[i] ?? '') !== h && !(tab === 'Cuentas' && i === USE_COL && have[i] === USE_HEADER));
    if (bad >= 0) block({ code: 'header-mismatch', tab, message: `${tab}: la columna ${bad + 1} debería llamarse "${expected[bad]}" y es "${have[bad] ?? ''}"` });
  }

  // Columnas calculadas (solo las celdas que se hayan leído)
  const damaged = Object.keys(formulas).filter(cell => FORMULAS[cell] !== undefined && normalizeFormula(formulas[cell]!) !== normalizeFormula(FORMULAS[cell]!));
  const missing = Object.keys(FORMULAS).filter(cell => cell in formulas === false && Object.keys(formulas).some(k => k.split('!')[0] === cell.split('!')[0]));
  if (damaged.length || missing.length) {
    block({ code: 'formulas-damaged', message: `Columnas calculadas dañadas: ${[...damaged, ...missing].join(', ')}`, repair: { kind: 'rewrite-formulas', cells: [...damaged, ...missing] } });
  }

  // Archivo
  if (wb.control.archiveJournal) block({ code: 'archive-in-progress', message: `Archivado a medias: ${wb.control.archiveJournal}`, repair: { kind: 'resume-archive' } });
  const archived = new Set(wb.control.archivedYears);
  const lingering = wb.movements.filter(m => archived.has(Number(m.date.slice(0, 4))));
  if (lingering.length) block({ code: 'archived-year-in-movements', tab: 'Movimientos', rows: lingering.map(m => m.row), message: `Hay ${lingering.length} movimientos de años ya archivados en Movimientos` });

  // Ids
  const byId = new Map<string, number[]>();
  wb.movements.forEach(m => { if (m.id) byId.set(m.id, [...(byId.get(m.id) ?? []), m.row]); });
  const dup = [...byId.entries()].filter(([, rows]) => rows.length > 1);
  if (dup.length) {
    const referenced = new Set(wb.parts.map(p => p.movementId));
    const safe = dup.filter(([id]) => !referenced.has(id)).flatMap(([, rows]) => rows.slice(1));
    block({ code: 'duplicate-ids', tab: 'Movimientos', rows: dup.flatMap(([, rows]) => rows),
      message: `Ids repetidos: ${dup.map(([id]) => id).join(', ')}${safe.length < dup.flatMap(([, r]) => r.slice(1)).length ? ' (alguno lo usan las Divisiones: revísalo a mano)' : ''}`,
      ...(safe.length ? { repair: { kind: 're-id-duplicate' as const, rows: safe } } : {}) });
  }
  if (wb.orphanIds.length) warn({ code: 'orphan-ids', tab: 'Movimientos', rows: wb.orphanIds.map(o => o.row),
    message: `${wb.orphanIds.length} Ids en filas vacías (no corresponden a ningún movimiento)`, repair: { kind: 'clear-orphan-ids', rows: wb.orphanIds.map(o => o.row) } });
  const noId = wb.movements.filter(m => !m.id);
  if (wb.missingTabs?.includes('Notas')) warn({ code: 'missing-notes-tab', message: 'Falta la pestaña Notas (para explicar un mes raro de una categoría)', repair: { kind: 'add-notes-tab' } });
  // Movimientos debajo de filas vacías (antes la app añadía tras las filas preparadas): no se ven al abrir la hoja
  const rows = wb.movements.map(m => m.row).sort((a, b) => a - b);
  if (rows.length && rows[rows.length - 1]! > rows.length + 1) {
    const stranded = rows.filter((r, i) => r !== i + 2);
    warn({ code: 'movements-gap', tab: 'Movimientos', rows: stranded,
      message: `${stranded.length} movimientos están debajo de filas vacías (desde la fila ${stranded[0]}): súbelos para tenerlos juntos`, repair: { kind: 'compact-movements' } });
  }
  if (noId.length) warn({ code: 'missing-ids', tab: 'Movimientos', rows: noId.map(m => m.row), message: `${noId.length} filas sin Id (añadidas a mano)`, repair: { kind: 'assign-ids', rows: noId.map(m => m.row) } });

  // Referencias
  const accounts = new Set(wb.accounts.map(a => a.name));
  const unknownAcc = wb.movements.filter(m => !accounts.has(m.account));
  if (unknownAcc.length) warn({ code: 'unknown-account', tab: 'Movimientos', rows: unknownAcc.map(m => m.row), message: `Cuenta que no existe en Cuentas: ${[...new Set(unknownAcc.map(m => m.account || '(vacía)'))].join(', ')}` });
  const cats = new Set(wb.categories.map(c => c.name.toLowerCase()));
  const unknownCat = wb.movements.filter(m => m.category && !cats.has(m.category.toLowerCase()));
  if (unknownCat.length) warn({ code: 'unknown-category', tab: 'Movimientos', rows: unknownCat.map(m => m.row), message: `Categorías que no existen (cuentan como "Sin categoría"): ${[...new Set(unknownCat.map(m => m.category))].join(', ')}` });
  for (const p of wb.problems) warn({ code: 'invalid-cell', tab: p.tab, rows: [p.row], message: `${p.tab} fila ${p.row}: ${p.message}` });
  const badMonth = wb.movements.filter(m => m.month && !/^\d{4}-(0[1-9]|1[0-2])$/.test(m.month));
  if (badMonth.length) warn({ code: 'invalid-month', tab: 'Movimientos', rows: badMonth.map(m => m.row), message: '"Mes (opcional)" debe tener el formato 2026-10' });
  const badShare = wb.accounts.filter(a => !(a.share >= 0 && a.share <= 1));
  if (badShare.length) warn({ code: 'invalid-share', tab: 'Cuentas', message: `Propiedad fuera de 0–100 %: ${badShare.map(a => a.name).join(', ')}` });

  // Divisiones
  const movById = new Map(wb.movements.filter(m => m.id).map(m => [m.id, m]));
  const partsBy = new Map<string, typeof wb.parts>();
  wb.parts.forEach(p => partsBy.set(p.movementId, [...(partsBy.get(p.movementId) ?? []), p]));
  partsBy.forEach((ps, id) => {
    const m = movById.get(id);
    const rows = ps.map(p => p.row);
    if (!m) return warn({ code: 'part-missing-movement', tab: 'Divisiones', rows, message: `División de un Id que no existe: ${id}` });
    const total = Math.round(ps.reduce((s, p) => s + p.amount, 0) * 100) / 100;
    if (Math.abs(total - m.amount) >= 0.005) warn({ code: 'part-sum-mismatch', tab: 'Divisiones', rows, message: `Las partes de ${id} suman ${total} y el movimiento es ${m.amount}` });
    if (m.category.toLowerCase() !== DIVIDED.toLowerCase()) warn({ code: 'part-not-divided', tab: 'Movimientos', rows: [m.row], message: `${id} tiene divisiones pero su categoría no es "${DIVIDED}" (contaría dos veces)` });
  });

  // Grupos
  const groups = new Map<string, typeof wb.movements>();
  wb.movements.forEach(m => { if (m.group) { const k = m.group.toLowerCase(); groups.set(k, [...(groups.get(k) ?? []), m]); } });
  groups.forEach(ms => {
    const expenses = ms.filter(m => m.amount < 0 && m.category);
    if (!expenses.length) warn({ code: 'group-without-expense', tab: 'Movimientos', rows: ms.map(m => m.row), message: `Grupo "${ms[0]!.group}" sin gasto con categoría: sus reembolsos quedan sin categoría` });
    else if (expenses.length > 1) warn({ code: 'group-many-expenses', tab: 'Movimientos', rows: ms.map(m => m.row), message: `Grupo "${ms[0]!.group}" con ${expenses.length} gastos: los reembolsos heredan del primero` });
  });

  // Presupuesto
  const seen = new Set<string>();
  wb.budget.forEach(b => {
    const k = `${b.year}|${b.category.toLowerCase()}`;
    if (seen.has(k)) warn({ code: 'budget-duplicate', tab: 'Presupuesto', message: `Presupuesto repetido: ${b.category} ${b.year}` });
    seen.add(k);
    if (!cats.has(b.category.toLowerCase()) && b.category !== SAVINGS_GOAL) warn({ code: 'budget-unknown-category', tab: 'Presupuesto', message: `Presupuesto de una categoría que no existe: ${b.category}` });
  });

  const blocking = issues.filter(i => i.level === 'blocking'), warnings = issues.filter(i => i.level === 'warning');
  return { ok: !blocking.length, blocking, warnings };
}
